<template>
  <!-- Teleport: the overlay lives inside .board-stage. A battle shake
       puts a transform on that column, and position:fixed then shrinks
       to the 128px compact board — the 5th Big Eye card falls off. -->
  <Teleport to="body">
  <div
    v-if="visible"
    class="card-picker"
    @touchstart.stop
    @mousedown.stop
    @pointerdown="onRootPointerDown"
    @pointerup="onRootPointerUp"
  >
    <div
      class="card-picker-panel"
      :class="{ split }"
      @pointerdown.stop
      @pointerup.stop
    >
      <div class="card-picker-body">
        <div v-if="source" class="card-picker-source">
          <CardArt
            :card-id="source.cardId"
            :foil="source.foil"
            :alt="source.name"
            intensity="thumb"
          />
          <span>{{ t('duel.picker.fromCard', { card: source.name }) }}</span>
        </div>
        <h2 class="card-picker-title">{{ title }}</h2>
        <p v-if="hint" class="card-picker-owner-hint">{{ hint }}</p>
        <p v-if="max > 1 || requireConfirm" class="card-picker-hint">
          {{
            min === max
              ? t('duel.picker.selectExact', { n: min })
              : t('duel.picker.selectRange', { min, max })
          }}
          ({{ selected.length }}/{{ max }})
        </p>
        <div :class="split ? 'card-picker-split' : undefined">
          <section
            v-for="group in groups"
            :key="group.key"
            class="card-picker-side"
            :class="group.key"
          >
            <h3 v-if="split && group.key !== 'rest'">
              {{ group.key === 'me' ? t('duel.gyYours') : t('duel.gyOpp') }}
            </h3>
            <p v-if="split && group.items.length === 0" class="card-picker-empty">
              {{ t('duel.picker.gyEmpty') }}
            </p>
            <div v-else class="card-picker-grid">
              <button
                v-for="item in group.items"
                :key="item.uid"
                type="button"
                class="card-picker-card"
                :class="{
                  selected: selected.includes(item.uid),
                  dimmed:
                    max > 1 && selected.length >= max && !selected.includes(item.uid),
                  leftover: leftoverUid === item.uid,
                  'is-mine': item.owner === 'me',
                  'is-opp': item.owner === 'opp',
                }"
                @click="onClick(item.uid)"
                @pointerdown="onPointerDown($event, item)"
                @pointerup="onPointerUp($event, item.uid)"
                @pointercancel="clearDown"
              >
                <span v-if="item.owner" class="card-picker-owner">
                  {{ item.owner === 'me' ? t('duel.picker.yours') : t('duel.picker.opp') }}
                </span>
                <CardArt
                  :card-id="item.cardId"
                  :foil="item.foil"
                  :alt="item.name"
                  intensity="thumb"
                />
                <span
                  v-if="selected.includes(item.uid)"
                  class="card-picker-check"
                >{{ selected.indexOf(item.uid) + 1 }}</span>
                <span v-if="item.note" class="card-picker-note">{{ item.note }}</span>
              </button>
            </div>
          </section>
        </div>
      </div>
      <div class="card-picker-actions">
        <button
          v-if="(max > 1 || requireConfirm) && !viewOnly"
          type="button"
          class="card-picker-btn confirm"
          :disabled="!canConfirm"
          @click="emitConfirm"
        >
          {{ t('duel.picker.confirm') }}
        </button>
        <button
          v-if="viewOnly"
          type="button"
          class="card-picker-btn confirm"
          @pointerup="onClosePointerUp"
          @click="dismissViewOnly"
        >
          {{ t('duel.picker.close') }}
        </button>
        <button
          v-else-if="min <= 0"
          type="button"
          class="card-picker-btn cancel"
          @click="$emit('cancel')"
        >
          {{ t('duel.picker.cancel') }}
        </button>
      </div>
    </div>
  </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from '../i18n/t'
import CardArt from './CardArt.vue'

const { t } = useI18n()

export interface CardPickerItem {
  uid: string
  cardId: string
  name?: string
  note?: string
  foil?: string
  /** Graveyard this card comes from, relative to the local player. */
  owner?: 'me' | 'opp'
}

const props = withDefaults(
  defineProps<{
    visible: boolean
    title: string
    items: CardPickerItem[]
    min?: number
    max?: number
    /** Review overlay: inspect cards, dismiss with Close. */
    viewOnly?: boolean
    /** UIDs already chosen when the overlay opens. */
    preselect?: string[]
    /** Card whose effect opened this picker. */
    source?: { cardId: string; name: string; foil?: string }
    /** Extra line under the title. */
    hint?: string
    /** Keep Confirm even when max is 1. */
    requireConfirm?: boolean
  }>(),
  { min: 1, max: 1, viewOnly: false, preselect: () => [], requireConfirm: false },
)

const split = computed(() =>
  props.items.some((item) => item.owner === 'me' || item.owner === 'opp'),
)

const groups = computed(() => {
  if (!split.value) return [{ key: 'all', items: props.items }]
  const me = props.items.filter((item) => item.owner === 'me')
  const opp = props.items.filter((item) => item.owner === 'opp')
  const rest = props.items.filter((item) => item.owner !== 'me' && item.owner !== 'opp')
  const rows = [
    { key: 'me', items: me },
    { key: 'opp', items: opp },
  ]
  if (rest.length > 0) rows.push({ key: 'rest', items: rest })
  return rows
})

const emit = defineEmits<{
  pick: [uid: string]
  confirm: [uids: string[]]
  cancel: []
  hover: [item: CardPickerItem]
}>()

const selected = ref<string[]>([])
let down: { uid: string; x: number; y: number; touch: boolean } | null = null
let swallowClick = false
let backdropDown = false
let dismissed = false
/**
 * Closing the search reveal swaps this list for the next choice in the
 * same gesture. The click that follows pointerup lands on whatever card
 * is now under the button, so Last Will or the second Sangan would take
 * a monster the player never chose.
 */
let ignorePicksUntil = 0

/** Big Eye: every candidate goes back. After n−1 taps the last slot is forced. */
const mustOrderAll = computed(
  () =>
    !props.viewOnly &&
    props.min > 1 &&
    props.min === props.max &&
    props.items.length === props.max,
)

function leftoverUids(picked: string[]): string[] {
  const have = new Set(picked)
  return props.items.map((item) => item.uid).filter((uid) => !have.has(uid))
}

function fillLastIfNeeded(): void {
  if (!mustOrderAll.value) return
  const left = leftoverUids(selected.value)
  if (left.length === 1 && left[0]) selected.value.push(left[0])
}

const leftoverUid = computed(() => {
  if (!mustOrderAll.value) return null
  const left = leftoverUids(selected.value)
  return left.length === 1 ? left[0] : null
})

const canConfirm = computed(() => {
  if (selected.value.length >= props.min) return true
  return mustOrderAll.value && selected.value.length === props.min - 1
})

function emitConfirm(): void {
  if (!canConfirm.value) return
  const uids = [...selected.value]
  if (mustOrderAll.value) {
    for (const uid of leftoverUids(uids)) uids.push(uid)
  }
  emit('confirm', uids)
}

watch(
  () => props.visible,
  (open) => {
    selected.value = open ? [...(props.preselect ?? [])] : []
    down = null
    swallowClick = false
    backdropDown = false
    dismissed = false
  },
  { immediate: true },
)

function dismissViewOnly(): void {
  if (!props.viewOnly || dismissed) return
  dismissed = true
  ignorePicksUntil = performance.now() + 400
  emit('cancel')
}

function strayPick(): boolean {
  return performance.now() < ignorePicksUntil
}

const onCardClick = (uid: string): void => {
  if (strayPick()) return
  if (props.viewOnly) {
    dismissViewOnly()
    return
  }
  if (props.max <= 1 && !props.requireConfirm) {
    emit('pick', uid)
    return
  }
  const at = selected.value.indexOf(uid)
  if (at !== -1) selected.value.splice(at, 1)
  else if (selected.value.length < props.max) {
    selected.value.push(uid)
    fillLastIfNeeded()
  } else if (props.max === 1) {
    selected.value = [uid]
  }
}

function clearDown(): void {
  down = null
}

function onPointerDown(e: PointerEvent, item: CardPickerItem): void {
  if (e.pointerType === 'mouse') swallowClick = false
  emit('hover', item)
  down = {
    uid: item.uid,
    x: e.clientX,
    y: e.clientY,
    touch: e.pointerType !== 'mouse',
  }
}

/**
 * A finger on a just-opened list often never produces a click (the activation
 * art or the scroll container ate it). pointerup still fires. The click that
 * follows is ignored so it cannot also hit the position sheet underneath.
 */
function onPointerUp(e: PointerEvent, uid: string): void {
  const start = down
  down = null
  if (!start?.touch || start.uid !== uid) return
  const dx = e.clientX - start.x
  const dy = e.clientY - start.y
  if (dx * dx + dy * dy > 144) return
  swallowClick = true
  onCardClick(uid)
}

function onClick(uid: string): void {
  if (swallowClick) {
    swallowClick = false
    return
  }
  onCardClick(uid)
}

function onRootPointerDown(e: PointerEvent): void {
  backdropDown = e.target === e.currentTarget
}

function onRootPointerUp(e: PointerEvent): void {
  if (!backdropDown) return
  backdropDown = false
  if (e.target !== e.currentTarget) return
  dismissViewOnly()
}

/** iPhone often never fires click on this button; pointerup still does. */
function onClosePointerUp(e: PointerEvent): void {
  if (e.pointerType === 'mouse' && e.button !== 0) return
  dismissViewOnly()
}
</script>

<style scoped>
.card-picker {
  position: fixed;
  inset: 0;
  /* Above the duel rails (100) and the activation flash (400). */
  z-index: 5000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding:
    max(12px, env(safe-area-inset-top, 0px))
    max(12px, env(safe-area-inset-right, 0px))
    max(12px, env(safe-area-inset-bottom, 0px))
    max(12px, env(safe-area-inset-left, 0px));
  background: rgba(0, 0, 0, 0.72);
  pointer-events: auto;
}

.card-picker-panel {
  display: flex;
  flex-direction: column;
  width: min(720px, calc(100% - 16px));
  max-width: min(720px, 92%);
  max-height: min(90svh, calc(100dvh - 24px));
  min-height: 0;
  overflow: hidden;
  padding: 16px 18px 14px;
  border-radius: 10px;
  background: #0f172a;
  border: 2px solid #88aaff;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.55);
  text-align: center;
}

.card-picker-panel.split {
  max-width: min(920px, 96%);
}

.card-picker-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}

.card-picker-source {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 0 0 10px;
  color: #93c5fd;
  font-size: 14px;
  font-weight: 600;
}

.card-picker-source :deep(.card-art) {
  width: 36px;
  height: auto;
  aspect-ratio: 421 / 614;
  border-radius: 3px;
  box-shadow: 0 0 0 1px #88aaff;
}

.card-picker-title {
  margin: 0 0 10px;
  color: #f8fafc;
  font-size: 18px;
}

.card-picker-owner-hint {
  margin: -4px 0 10px;
  color: #fde68a;
  font-size: 13px;
  font-weight: 600;
}

.card-picker-hint {
  margin: 0 0 10px;
  color: #ffd54f;
  font-size: 13px;
}

.card-picker-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
}

.card-picker-split {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  align-items: start;
}

.card-picker-split .card-picker-side {
  min-width: 0;
  padding: 8px 8px 10px;
  border-radius: 8px;
  border: 2px solid transparent;
}

.card-picker-split .card-picker-side.me {
  border-color: #38bdf8;
  background: rgba(14, 116, 144, 0.28);
}

.card-picker-split .card-picker-side.opp {
  border-color: #f87171;
  background: rgba(127, 29, 29, 0.35);
}

.card-picker-side h3 {
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 700;
}

.card-picker-side.me h3 {
  color: #7dd3fc;
}

.card-picker-side.opp h3 {
  color: #fca5a5;
}

.card-picker-empty {
  margin: 12px 0;
  color: #94a3b8;
  font-size: 13px;
}

.card-picker-card {
  position: relative;
  z-index: 1;
  width: clamp(58px, 22vw, 92px);
  aspect-ratio: 421 / 614;
  padding: 0;
  border: 2px solid #66ccff;
  border-radius: 6px;
  background: #223344;
  cursor: pointer;
  overflow: visible;
  transition: transform 0.12s ease, opacity 0.12s ease, box-shadow 0.12s ease,
    border-color 0.12s ease, filter 0.12s ease;
}

.card-picker-card.is-opp {
  border-color: #f87171;
}

.card-picker-owner {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2;
  padding: 2px 0 3px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #f8fafc;
  pointer-events: none;
}

.card-picker-card.is-mine .card-picker-owner {
  background: #0369a1;
}

.card-picker-card.is-opp .card-picker-owner {
  background: #b91c1c;
}

.card-picker-card:hover:not(.selected) {
  border-color: #93c5fd;
  box-shadow: 0 0 10px rgba(147, 197, 253, 0.5);
}

.card-picker-card:active {
  scale: 0.94;
}

.card-picker-btn:active:not(:disabled) {
  scale: 0.96;
  filter: brightness(1.15);
}

.card-picker-card.selected {
  z-index: 0;
  transform: scale(1.05);
  border-width: 4px;
  border-color: #facc15;
  box-shadow:
    0 0 0 3px #14532d,
    0 0 18px 4px rgba(250, 204, 21, 0.95),
    0 0 36px rgba(250, 204, 21, 0.55);
  filter: brightness(1.12);
}

.card-picker-card.leftover {
  z-index: 2;
  border-color: #fde68a;
}

.card-picker-card.selected::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 4px;
  pointer-events: none;
  box-shadow: inset 0 0 0 3px #facc15;
  background: linear-gradient(
    180deg,
    rgba(250, 204, 21, 0.28) 0%,
    transparent 42%
  );
}

.card-picker-card.dimmed {
  opacity: 0.4;
  filter: grayscale(0.35);
}

.card-picker-check {
  position: absolute;
  top: -10px;
  right: -10px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 26px;
  height: 26px;
  padding: 0 6px;
  border-radius: 999px;
  background: #14532d;
  border: 2px solid #facc15;
  color: #facc15;
  font-size: 13px;
  font-weight: 800;
  line-height: 1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.55);
}

.card-picker-card :deep(.card-art) {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: 4px;
}

.card-picker-note {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 2px 4px;
  overflow: hidden;
  font-size: 10px;
  white-space: nowrap;
  text-overflow: ellipsis;
  pointer-events: none;
  color: #ffd54f;
  background: #000000aa;
}

.card-picker-actions {
  display: flex;
  flex: 0 0 auto;
  justify-content: center;
  gap: 10px;
  margin-top: 14px;
}

.card-picker-btn {
  padding: 6px 14px;
  border-radius: 5px;
  border: 1px solid #94a3b8;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.card-picker-btn.confirm {
  min-width: 120px;
  min-height: 44px;
  padding: 10px 20px;
  background: #14532d;
  border-color: #86efac;
  color: #86efac;
}

.card-picker-btn.confirm:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.card-picker-btn.cancel {
  background: transparent;
  color: #fca5a5;
  border-color: transparent;
}

@media (max-width: 560px) and (orientation: portrait) {
  .card-picker-split {
    grid-template-columns: 1fr;
  }
}

@media (max-height: 500px) {
  .card-picker-split {
    grid-template-columns: 1fr;
  }
}

@media (max-height: 430px) {
  .card-picker-panel {
    padding: 10px 12px 8px;
  }

  .card-picker-source {
    margin-bottom: 4px;
  }

  .card-picker-title {
    margin-bottom: 4px;
    font-size: 16px;
  }

  .card-picker-owner-hint {
    margin-bottom: 6px;
    font-size: 12px;
  }

  .card-picker-split .card-picker-card {
    width: 72px;
  }

  .card-picker-side h3 {
    margin-bottom: 4px;
    font-size: 13px;
  }

  .card-picker-actions {
    margin-top: 8px;
  }
}
</style>
